import React from 'react'
import { useMsal } from '@azure/msal-react'
import Text from '@embracesbs/component-text'
import RadioButton from '@embracesbs/component-radiobutton'
import Autocomplete from '@embracesbs/component-autocomplete'
import Switch from '@embracesbs/component-switch'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Toast from '@embracesbs/component-toast'
import SectionHeader from '../Components/Common/SectionHeader'
import SectionCard from '../Components/Common/SectionCard'
import { useUserPreferences } from '../Hooks'
import { AccessibilityTheme, ThemeMode } from '../Components/Context/PreferencesContext'
import { displayName, initials, avatarHue } from '../Utilities/userDisplay'

const APPEARANCE_OPTIONS = [
  { id: 'appearance-light', value: 'light', title: 'Light' },
  { id: 'appearance-dark', value: 'dark', title: 'Dark' },
  { id: 'appearance-system', value: 'system', title: 'System', description: 'Follow your operating system setting' }
]

const ACCESSIBILITY_OPTIONS: { label: string; value: AccessibilityTheme }[] = [
  { label: 'None', value: 'none' },
  { label: 'High contrast', value: 'a11y' },
  { label: 'Deuteranopia (red-green)', value: 'deuteranopia' },
  { label: 'Protanopia (red-green)', value: 'protanopia' },
  { label: 'Tritanopia (blue-yellow)', value: 'tritanopia' },
  { label: 'Monochromacy', value: 'monochromacy' }
]

// Live color preview: swatches reference the --tma-* tokens directly, so they
// re-render the moment the color vision theme changes them (index.css).
const STATUS_PREVIEW: { label: string; description: string; color: string }[] = [
  { label: 'Finished', description: 'Deploy succeeded', color: 'var(--tma-status-ok)' },
  { label: 'Failed', description: 'Deploy or update failed, delete pending', color: 'var(--tma-status-failed)' },
  { label: 'Updating', description: 'Update pipeline is running', color: 'var(--tma-status-progress)' },
  { label: 'Waiting', description: 'Waiting on the frontend deploy', color: 'var(--tma-status-wait)' },
  { label: 'Initializing', description: 'New tenant being created', color: 'var(--tma-status-info)' },
  { label: 'Unknown', description: 'Status not reported', color: 'var(--tma-status-unknown)' }
]

const SEMANTIC_PREVIEW: { label: string; text: string; tint: string }[] = [
  { label: 'Success', text: 'var(--tma-success-text)', tint: 'var(--tma-success-tint)' },
  { label: 'Warning', text: 'var(--tma-warning-text)', tint: 'var(--tma-warning-tint)' },
  { label: 'Danger', text: 'var(--tma-danger-text)', tint: 'var(--tma-danger-tint)' }
]

// Mirrors the tenant list's status block (letter on a tinted band).
const previewSwatchStyle = (color: string): React.CSSProperties => ({
  width: '34px',
  height: '22px',
  borderRadius: '4px',
  flexShrink: 0,
  background: `color-mix(in srgb, ${color} 13%, transparent)`,
  borderLeft: `3px solid ${color}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
})

// Per-user settings: appearance (light/dark/system), accessibility theme and
// calm mode. Changes apply immediately and are saved via /api/preferences.
const Settings: React.FC = () => {
  const { preferences, update } = useUserPreferences()
  const { accounts } = useMsal()
  const account = accounts[0]
  const email = account?.username || ''
  const hasA11yTheme = preferences.accessibilityTheme !== 'none'

  return (
    <div
      style={{ height: 'var(--app-height, 100vh)', overflowY: 'auto', padding: '24px 40px', boxSizing: 'border-box' }}
      className='custom-scrollbar'
    >
      <Toast />
      <div style={{ display: 'flex', gap: '28px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '720px', flex: '1 1 480px' }}>
          <SectionHeader title='Settings' />

          {/* Signed in as */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0 20px' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: `hsl(${avatarHue(email)}, 45%, 55%)`,
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              {initials(email)}
            </div>
            <div>
              <Text textStyle='body' content={account?.name || displayName(email)} style={{ fontWeight: 600 }} />
              <Text textStyle='description' textColor='grey' content={email} />
            </div>
          </div>

          <SectionCard title='Appearance'>
            <RadioButton
              name='appearance'
              orientation='vertical'
              options={APPEARANCE_OPTIONS}
              selected={preferences.theme}
              isDisabled={hasA11yTheme}
              // The design system's RadioButton emits onClick(event, value); its
              // onChange prop is consumed internally and never fires.
              onClick={(_: React.MouseEvent, value: string) => update({ theme: value as ThemeMode })}
            />
            {hasA11yTheme && (
              <Text
                textStyle='description'
                textColor='grey'
                content='Appearance is fixed to light while an accessibility theme is active.'
                style={{ marginTop: '8px' }}
              />
            )}
          </SectionCard>

          <SectionCard title='Accessibility'>
            <TextFieldWrapper labelText='Color vision theme'>
              <Autocomplete
                value={preferences.accessibilityTheme}
                options={ACCESSIBILITY_OPTIONS}
                onChange={(e: { value: AccessibilityTheme }) => update({ accessibilityTheme: e.value })}
              />
            </TextFieldWrapper>
          </SectionCard>

          <SectionCard title='Readability'>
            <Switch
              name='calm-mode'
              label='Calm tenant details'
              isChecked={preferences.calmMode}
              onClick={() => update({ calmMode: !preferences.calmMode })}
            />
            <Text
              textStyle='description'
              textColor='grey'
              content='Adds spacing and a narrower reading width to the tenant Deploytime form.'
              style={{ marginTop: '8px' }}
            />
          </SectionCard>
        </div>

        {/* Live color preview — the swatches read the theme tokens, so they
            update instantly while trying out a color vision theme. */}
        <div style={{ width: '320px', flexShrink: 0, position: 'sticky', top: 0 }}>
          <SectionHeader title='Color preview' />
          <SectionCard title='Tenant statuses'>
            <Text
              textStyle='description'
              textColor='grey'
              content='How each deploy status is colored with the selected color vision theme.'
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
              {STATUS_PREVIEW.map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={previewSwatchStyle(s.color)}>
                    <span style={{ color: s.color, fontSize: '11px', fontWeight: 700 }}>A</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <Text textStyle='body' content={s.label} style={{ fontSize: '13px', fontWeight: 600 }} />
                    <Text textStyle='description' textColor='grey' content={s.description} style={{ fontSize: '11px' }} />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard title='Alerts and results'>
            <Text textStyle='description' textColor='grey' content='Used by warnings, danger zones and success notes across the app.' />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              {SEMANTIC_PREVIEW.map((s) => (
                <span
                  key={s.label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: s.text,
                    background: s.tint
                  }}
                >
                  {s.label}
                </span>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default Settings
